/**
 * Provide/inject keys for non-substrate composables.
 *
 * The substrate state (variant, journey, api, speedtest, sessionExpiry)
 * lives in Pinia stores — those don't appear here. The composables that
 * remain provider-based are per-app stateful instances
 * (`useGeolocation`, `useIPInfo`, `useConnectionType`) whose constructor
 * belongs in App.vue's setup, plus the dock-start / forward callbacks
 * which are closures over App.vue's cellular-warning policy.
 */

import type { InjectionKey } from "vue";
import type { useIPInfo } from "./useIPInfo.ts";
import type { useGeolocation } from "./useGeolocation.ts";
import type { useConnectionType } from "./useConnectionType.ts";

export const IPInfoKey: InjectionKey<ReturnType<typeof useIPInfo>> =
    Symbol("ipInfoProvider");
export const GeolocationKey: InjectionKey<ReturnType<typeof useGeolocation>> =
    Symbol("geolocation");
export const ConnectionTypeKey: InjectionKey<ReturnType<typeof useConnectionType>> =
    Symbol("connectionType");

/** Invokes the canonical speedtest start flow (cellular preflight + session + start).
 *  Provided by App.vue; injected by any view that needs to trigger a test. */
export const DockStartKey: InjectionKey<() => void | Promise<void>> =
    Symbol("dockStart");

/** Forward handler — post-test routes to /survey for survey variants and
 *  restarts the test for test-only variants. Wired through `useAppNavigation`
 *  so the bottom-dock's complete-state forward path stays one policy seam,
 *  not a re-implementation. */
export const DockForwardKey: InjectionKey<() => void | Promise<void>> =
    Symbol("dockForward");
